import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Volume2, ChevronRight, RotateCcw, Headphones } from "lucide-react";
import { play, preload, sfx, stopAudio } from "../../lib/audio";
import { Ruby } from "./Ruby";
import { storyIntro } from "./StoryPlayer";

const NextBtn = ({ onClick, children, testid }) => (
  <button data-testid={testid} onClick={onClick} className="kid-btn px-10 bg-[#3E9C8F] text-white text-2xl font-round flex items-center gap-2">{children} <ChevronRight /></button>
);

export const ListenStory = ({ scenes, title, onDone }) => {
  const lines = scenes.flatMap((s) => [
    { text: s.narration_text, ruby: s.ruby },
    ...(s.dialogue_text?.text ? [{ text: s.dialogue_text.text, ruby: s.dialogue_ruby, speaker: s.dialogue_text.speaker }] : []),
  ]);
  const [cur, setCur] = useState(-1);
  const token = useRef(0);
  const runFrom = async (start, intro) => {
    const my = ++token.current;
    if (intro) { await play(storyIntro(title)); if (my !== token.current) return; }
    for (let i = start; i < lines.length; i++) {
      setCur(i);
      await play(lines[i].text);
      if (my !== token.current) return;
      await new Promise((r) => setTimeout(r, 350));
      if (my !== token.current) return;
    }
    setCur(-1);
  };
  useEffect(() => { runFrom(0, true); return () => { token.current++; stopAudio(); }; }, []); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <div className="flex flex-col items-center h-full gap-5 py-3 font-round" data-testid="listen-story-step">
      <h2 className="text-3xl sm:text-4xl text-[#2c6e65] flex items-center gap-3"><Headphones /> 聽故事</h2>
      <div className="flex-1 w-full max-w-3xl overflow-y-auto bg-white rounded-[2rem] border-4 border-[#cfe8e2] p-5 sm:p-8 space-y-3">
        {lines.map((l, i) => (
          <button key={i} data-testid={`listen-line-${i}`} onClick={() => runFrom(i)}
            className={`w-full text-left rounded-2xl px-4 py-3 transition-colors flex gap-3 items-start ${cur === i ? "bg-amber-100 ring-4 ring-amber-300" : "hover:bg-[#eef7f4]"}`}>
            <Volume2 className={`shrink-0 mt-3 ${cur === i ? "text-amber-600" : "text-[#9cc9bf]"}`} />
            <span className="text-3xl sm:text-4xl leading-[1.9] text-stone-800">
              {l.speaker && <span className="text-xl text-[#3E9C8F] mr-2">{l.speaker}：</span>}
              <Ruby data={l.ruby?.length ? l.ruby : [...l.text].map((c) => [c, ""])} />
            </span>
          </button>
        ))}
      </div>
      <div className="flex gap-3">
        <button data-testid="listen-story-replay" onClick={() => runFrom(0, true)} className="kid-btn px-6 bg-white border-2 border-[#cfe8e2] text-[#2c6e65] text-xl font-round flex items-center gap-2"><RotateCcw /> 再聽一次</button>
        <NextBtn testid="listen-story-next" onClick={() => { token.current++; stopAudio(); onDone(); }}>學新詞</NextBtn>
      </div>
    </div>
  );
};

export const VocabStep = ({ list, onSeen, onDone }) => {
  const [i, setI] = useState(0);
  useEffect(() => { preload(list.map((v) => v.word)); onSeen(list.map((v) => ({ type: "word", key: v.word }))); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const v = list[i];
  useEffect(() => { if (v) play(v.word, v.audio_url); }, [i]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!v) return null;
  const next = () => { sfx("tap"); if (i < list.length - 1) setI(i + 1); else onDone(); };
  return (
    <div className="flex flex-col items-center justify-between h-full gap-6 py-4 font-round" data-testid="vocab-step">
      <div className="flex flex-wrap justify-center gap-2">
        {list.map((w, k) => (
          <button key={w.word_id} data-testid={`vocab-tab-${k}`} onClick={() => setI(k)}
            className={`h-11 px-4 rounded-full text-xl border-2 ${k === i ? "bg-[#3E9C8F] text-white border-transparent" : "bg-white border-[#cfe8e2] text-stone-700"}`}>{w.word}</button>
        ))}
      </div>
      <motion.button key={v.word_id} data-testid="vocab-card" onClick={() => play(v.word, v.audio_url)} initial={{ scale: 0.6, rotate: -4 }} animate={{ scale: 1, rotate: 0 }} transition={{ type: "spring" }} whileTap={{ scale: 0.95 }}
        className="bg-white rounded-[2.5rem] border-4 border-[#3E9C8F] shadow-xl px-10 sm:px-16 py-8 flex flex-col items-center gap-4">
        <span className="text-[7rem] sm:text-[9rem] leading-none">{v.emoji}</span>
        <Ruby data={v.ruby} className="text-6xl sm:text-7xl text-stone-900" testid="vocab-word" />
        <span className="flex items-center gap-2 text-[#3E9C8F] text-xl"><Volume2 /> 點我聽</span>
      </motion.button>
      <NextBtn testid="vocab-next-btn" onClick={next}>{i < list.length - 1 ? "下一個詞" : "學句型"}</NextBtn>
    </div>
  );
};

const Blank = ({ text }) => (
  <span>{text.split("＿＿").map((part, k, arr) => (
    <span key={k}>{part}{k < arr.length - 1 && <span className="inline-block w-16 sm:w-20 mx-1 border-b-4 border-dashed border-amber-400 align-middle" />}</span>
  ))}</span>
);

export const PatternStep = ({ list, onSeen, onDone }) => {
  const [i, setI] = useState(0);
  const token = useRef(0);
  const p = list[i];
  useEffect(() => { onSeen(list.map((x) => ({ type: "pattern", key: x.pattern }))); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!p) return;
    const my = ++token.current;
    (async () => { for (const e of p.examples) { await play(e); if (my !== token.current) return; await new Promise((r) => setTimeout(r, 400)); } })();
    return () => { token.current++; };
  }, [i]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!p) return null;
  const next = () => { sfx("tap"); stopAudio(); if (i < list.length - 1) setI(i + 1); else onDone(); };
  return (
    <div className="flex flex-col items-center justify-between h-full gap-6 py-4 font-round" data-testid="pattern-step">
      <div className="text-xl text-[#3E9C8F]">實用句型 {i + 1} / {list.length}</div>
      <motion.div key={p.pattern_id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-3xl flex flex-col items-center gap-6">
        <div className="bg-amber-50 border-4 border-amber-300 rounded-[2rem] px-8 py-6 text-4xl sm:text-5xl text-amber-900" data-testid="pattern-text"><Blank text={p.pattern} /></div>
        <div className="w-full space-y-3">
          {p.examples.map((e, k) => (
            <button key={e} data-testid={`pattern-example-${k}`} onClick={() => { token.current++; play(e); }}
              className="w-full bg-white rounded-2xl border-2 border-[#cfe8e2] px-5 py-3 flex items-center gap-3 hover:bg-[#eef7f4]">
              <Volume2 className="text-[#3E9C8F] shrink-0" />
              <Ruby data={p.examples_ruby?.[k] || [...e].map((c) => [c, ""])} className="text-3xl sm:text-4xl leading-[1.9] text-stone-800" />
            </button>
          ))}
        </div>
      </motion.div>
      <NextBtn testid="pattern-next-btn" onClick={next}>{i < list.length - 1 ? "下一個句型" : "去玩遊戲"}</NextBtn>
    </div>
  );
};
